// WHICH ORGANISATION the signed-in person is in, as state: the value the app chrome names on every page
// (owner's decision 3.11.2026), who may write it, and WHICH WRITE WINS when two of them race.
//
// Until the chrome named the organisation, the shell named it for ONE kind of reader: a person whose token
// carries memberships, through the tenant pill they switch with (ADR-0018 §6). Everybody else — the
// single-tenant majority — saw the product brand and nothing else, so a pääkäyttäjä of two customers'
// systems, or anyone looking at a screenshot, could not tell whose data was on screen.
//
// WHY A MODULE STORE AND NOT A CONTEXT. Two unrelated places in the tree need the value and one of them
// WRITES: the chrome (auth/tenantPicker.tsx `TenantIdentity`, in the top bar) reads it, and the
// organisation settings form (tenant/TenantOrganisationSettings.tsx, deep inside a route) has the new name
// and logo in its hands the moment a save answers — acceptance criterion 4 is that the saver sees the
// change at once, without a reload. A context would have had to be mounted above the router and threaded a
// setter down to the form; a store the form can simply call is the same thing with nothing to thread. It is
// the shape auth/tenantSelection.ts already uses for the one active tenant, and it dies with the document.
//
// WHY THIS IS A SEPARATE FILE FROM THE HOOK (tenant/tenantIdentity.ts). What is written here is ordering —
// two writers, one of them asynchronous — and the only honest test of ordering is one that holds a read
// open and then completes it late. ADR-0002 forbids `page.route` on sight, so an E2E run cannot hold the
// branding GET open (e2e/guard/realstack-no-network-mocks.test.mjs is the check), which makes this ADR-0002
// entry rule B logic: more orderings than a run can sample. A test can only exercise it if the module it
// imports pulls in neither React nor lib/api.ts, whose config.ts reads `import.meta.env` and therefore
// cannot be evaluated outside Vite. So the state and the ordering live here with the fetch INJECTED, the
// React hook and the real `apiFetch` live next door, and src/lib/tenant-identity-store.test.mjs drives
// this file directly — the same split src/changePrograms/capabilityReading.ts and its test use.
//
// WHY THE VALUE IS STAMPED WITH A TENANT (ADR-0018 §6.2). The read is tenant-scoped, and for a person whose
// token carries memberships WHICH tenant is a question they answer after signing in. A value read for one
// organisation must never be shown under another's rows — that is the whole hazard ADR-0018 §6 exists to
// remove — so everything stored here carries the `choiceKey` it was read for, and the reader compares it.

import type { components } from "@clms/contracts";
import { activeChoice, choiceKey } from "../auth/tenantSelection.ts";

/**
 * The organisation's name and logo as `GET /api/tenant/branding` answers them — the generated DTO, never
 * hand-written, so the chrome cannot drift from what the backend discloses.
 *
 * `logoUrl` is the ANONYMOUS serve route for an uploaded logo (`/api/auth/tenant/logo?tenant=…`), which is
 * what makes it usable in an `<img src>`: a browser fetching an image sends no `Authorization` header. Null
 * for an organisation that has stored no logo — the chrome then shows the name and its initial, never a
 * broken image.
 */
export type TenantOrganisationBranding = components["schemas"]["TenantOrganisationBrandingDto"];

/** What is held, and WHICH ORGANISATION it was held for (`choiceKey`). The pair is the point — see above. */
export interface KnownTenantIdentity {
  key: string;
  branding: TenantOrganisationBranding;
}

let known: KnownTenantIdentity | null = null;

/** The mounted readers. A Set, so a double-mounted StrictMode effect adds and removes one subscription. */
const listeners = new Set<() => void>();

/**
 * The read that is currently out, IDENTIFIED by a token and not merely keyed by its tenant.
 *
 * The token is the fix for the defect that failed this task's first round. Keying alone said "a read for
 * this tenant is out", which cannot tell one read for a tenant from the next read for the SAME tenant, nor
 * a read that is still wanted from one whose answer has since been superseded. Both happen:
 *
 *   * the settings form saves a rename while the chrome's branding GET is still out. That GET read the row
 *     BEFORE the save, so its answer carries the OLD name. Completing under a tenant-key comparison it
 *     still passes, it overwrote the saved name — and every later read returned early, because the key was
 *     already held — so the chrome reverted after a successful save and stayed stale until a reload.
 *   * a sign-out and a new sign-in in the same document (a shared browser, and the E2E harness, which seeds
 *     sessions into a live page) start a second read under the same key while the first is still out.
 *
 * With a token, {@link publishTenantIdentity} and {@link forgetTenantIdentity} drop the outstanding read by
 * clearing this, and a read commits only while it is still THE outstanding one. An answer that lost the
 * race lands nowhere rather than on top of the answer that won.
 */
let outstanding: { key: string; token: number } | null = null;

/** Monotonic, so no two reads of this document's lifetime are ever confused for each other. */
let nextToken = 1;

function announce(): void {
  for (const listener of [...listeners]) listener();
}

/** What is held right now, for a reader taking its first snapshot. */
export function currentTenantIdentity(): KnownTenantIdentity | null {
  return known;
}

/** Subscribe to every change. Returns the unsubscribe. */
export function subscribeToTenantIdentity(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Record branding the caller already holds, as the organisation in force.
 *
 * The one caller is the organisation settings form: every write it makes answers with the settings row it
 * just wrote, so the new name and the new logo URL are in its hands and a second request would only ask the
 * server what it has just been told. Acceptance criterion 4 — the saver sees the change in the chrome
 * immediately — is this call.
 *
 * STAMPED WITH THE ACTIVE TENANT and not with whatever this store last read: the form is a page of the
 * organisation in force, so `choiceKey(activeChoice())` is the organisation it just wrote to. That is the
 * same value `useActiveTenantKey()` answers once the tenant is settled, which is why a value published here
 * is readable by the hook rather than being filed under a key nothing matches.
 *
 * IT DROPS THE OUTSTANDING READ, which is not housekeeping: a branding GET that is still out read the
 * settings row before this save and would otherwise complete on top of it. See {@link outstanding}.
 */
export function publishTenantIdentity(branding: TenantOrganisationBranding): void {
  known = { key: choiceKey(activeChoice()), branding };
  outstanding = null;
  announce();
}

/**
 * Forget what is held — the session ended, or the tenant became an open question again.
 *
 * It is not housekeeping either: `choiceKey(null)` is `"none"`, the key of every person who is simply in
 * their own tenant, so a value left behind by one such session would be read as the next one's. Two people
 * signing in one after the other in the same document — a shared browser, and the E2E harness — is exactly
 * that. Any read still out was asked for the tenant being left, so it is disowned with the value.
 */
export function forgetTenantIdentity(): void {
  if (known === null && outstanding === null) return;
  known = null;
  outstanding = null;
  announce();
}

/**
 * Read the organisation in force and hold the answer. Never throws: a failure leaves the chrome with
 * whatever it had.
 *
 * @param key the tenant this read is for — `useActiveTenantKey()`'s answer.
 * @param fetchBranding the request itself, passed in rather than imported; see this file's header for why.
 */
export async function loadTenantIdentity(
  key: string,
  fetchBranding: () => Promise<TenantOrganisationBranding>,
): Promise<void> {
  // Already answered for this organisation, or already asked: the chrome has several renders per load and
  // the answer does not change inside one.
  if (known?.key === key || outstanding?.key === key) return;

  const token = nextToken++;
  outstanding = { key, token };

  try {
    const branding = await fetchBranding();
    // THE ONE GUARD THAT MATTERS. Not "is a read for this tenant out" but "am I still it". A switch, a
    // sign-out or a save published by the settings form has cleared or replaced this, and in each of those
    // cases what arrived here is now the stale half of a race: storing it would name the organisation this
    // document has just left, or put the pre-save name back over the saved one.
    if (outstanding?.token !== token) return;
    known = { key, branding };
    outstanding = null;
    announce();
  } catch {
    // An outage, a 401 the session hook is already handling, a load that was navigated away from. Nothing
    // is claimed to the reader and nothing is retried: the chrome falls back to the name the tenant choice
    // carries, and the next read asks again — which is why the slot is released, but only if it is still
    // ours to release.
    if (outstanding?.token === token) outstanding = null;
  }
}
